<template>
  <div class="modal-mask" @click.self="fechar">
    <div class="modal-conteudo">
      <h3>Detalhes da OS</h3>

      <div v-if="carregando" class="loading-estado">Carregando…</div>
      <div v-else-if="erroCarregar" class="erro-estado">{{ erroCarregar }}</div>
      <template v-else-if="os">
        <div class="formulario">
          <div class="grid-info">
            <div class="campo">
              <label>Código</label>
              <p class="data-calculada">{{ os.codigo || "—" }}</p>
            </div>
            <div class="campo">
              <label>Status</label>
              <p class="data-calculada">{{ os.status || "—" }}</p>
            </div>
          </div>

          <div class="campo">
            <label>Pós-venda</label>
            <p class="data-calculada">{{ labelPosVenda }}</p>
          </div>

          <div class="campo">
            <label>Cliente</label>
            <p class="data-calculada">{{ clienteLabel }}</p>
          </div>

          <div class="campo">
            <label>NF de remessa do cliente <span class="obrigatorio">*</span></label>
            <input
              v-model="form.nf_entrada"
              type="text"
              placeholder="Número da NF de remessa do cliente"
              autocomplete="off"
              required
            />
          </div>

          <div class="campos-datas">
            <div class="campo">
              <label>Data prevista de recebimento do material</label>
              <input
                v-if="podeEditarDataPrevista"
                v-model="form.dt_prevista_recebimento_material"
                type="date"
                required
              />
              <p v-else class="data-calculada">
                {{ formatarDataBr(form.dt_prevista_recebimento_material) }}
              </p>
              <p v-if="!podeEditarDataPrevista" class="hint-data-calculada">
                Não editável após o recebimento do material.
              </p>
            </div>
            <div class="campo">
              <label>Data prevista de encerramento da OS</label>
              <p class="data-calculada">{{ formatarDataBr(dtFimPrevistoExibicao) }}</p>
              <p v-if="podeEditarDataPrevista" class="hint-data-calculada">
                Recalculada automaticamente (7 dias úteis após a data prevista de recebimento).
              </p>
            </div>
          </div>

          <div class="campo">
            <label>Observações</label>
            <p class="texto-leitura">{{ os.diagnostico?.trim() || "—" }}</p>
          </div>

          <div class="campo">
            <label>Produtos</label>
            <p v-if="!produtos.length" class="texto-na">Nenhum produto informado.</p>
            <table v-else class="tabela-produtos">
              <colgroup>
                <col />
                <col class="col-qtd" />
              </colgroup>
              <thead>
                <tr>
                  <th class="th-produto">Produto</th>
                  <th class="th-qtd">Qtd. descrita</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in produtos" :key="row.id || row.produto_cod">
                  <td class="td-produto">{{ resumoProduto(row) }}</td>
                  <td>{{ row.qt_descrita ?? "—" }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="acoes">
          <button type="button" class="acao-secundaria" :disabled="salvando" @click="fechar">
            Cancelar
          </button>
          <button type="button" class="acao-primaria" :disabled="salvando" @click="salvar">
            {{ salvando ? "Salvando…" : "Salvar" }}
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  atualizarCabecalhoOsAssistenciaPosVenda,
  calcularDtFimPrevisto7DiasUteis,
  obterOsAssistenciaDetalhe,
} from "../services/os2AssistenciaOrdemServicoService";

const STATUS_AGUARDANDO_RECEBIMENTO = "Aguardando recebimento";

function toDateOnly(val) {
  if (val == null || val === "") return "";
  const s = String(val).trim();
  if (s.length >= 10) return s.slice(0, 10);
  return "";
}

export default {
  name: "ModalDetalheOsAssistenciaPosVenda",
  props: {
    osId: {
      type: [Number, String],
      required: true,
    },
    posVendaLabel: {
      type: String,
      default: "",
    },
  },
  emits: ["fechar", "salvo"],
  data() {
    return {
      os: null,
      carregando: true,
      erroCarregar: "",
      salvando: false,
      form: {
        nf_entrada: "",
        dt_prevista_recebimento_material: "",
      },
    };
  },
  computed: {
    produtos() {
      return Array.isArray(this.os?.produtos) ? this.os.produtos : [];
    },
    clienteLabel() {
      const cli = this.os?.cliente;
      if (!cli) return "—";
      return cli.nome || cli.razao_social || "—";
    },
    labelPosVenda() {
      if (this.posVendaLabel) return this.posVendaLabel;
      const pv = this.os?.pos_venda ?? this.os?.posVenda;
      if (!pv) return "—";
      const codigo = pv.codigo ? `#${pv.codigo}` : `#${pv.id}`;
      return `Pós-venda ${codigo}`;
    },
    podeEditarDataPrevista() {
      return String(this.os?.status ?? "") === STATUS_AGUARDANDO_RECEBIMENTO;
    },
    dtFimPrevistoExibicao() {
      if (this.podeEditarDataPrevista) {
        return calcularDtFimPrevisto7DiasUteis(this.form.dt_prevista_recebimento_material);
      }
      return toDateOnly(this.os?.dt_fim_previsto);
    },
  },
  async created() {
    await this.carregar();
  },
  methods: {
    fechar() {
      this.$emit("fechar");
    },
    formatarDataBr(dataIso) {
      const iso = toDateOnly(dataIso);
      if (!iso) return "—";
      const [ano, mes, dia] = iso.split("-");
      if (!ano || !mes || !dia) return "—";
      return `${dia}/${mes}/${ano}`;
    },
    resumoProduto(row) {
      const p = row?.produto;
      const cod = p?.cod || p?.produto_cod || row?.produto_cod || "";
      const desc = p?.desc || "";
      if (cod && desc) return `${cod} — ${desc}`;
      return cod || desc || "—";
    },
    async carregar() {
      this.carregando = true;
      this.erroCarregar = "";
      try {
        const detalhe = await obterOsAssistenciaDetalhe(this.osId);
        this.os = detalhe;
        this.form = {
          nf_entrada: String(detalhe.nf_entrada ?? ""),
          dt_prevista_recebimento_material: toDateOnly(
            detalhe.dt_prevista_recebimento_material,
          ),
        };
      } catch (error) {
        console.error(error);
        this.erroCarregar =
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Não foi possível carregar os detalhes da OS.";
      } finally {
        this.carregando = false;
      }
    },
    validar() {
      if (!String(this.form.nf_entrada || "").trim()) {
        this.notificar("error", "Informe a NF de remessa do cliente.");
        return false;
      }
      if (this.podeEditarDataPrevista && !this.form.dt_prevista_recebimento_material) {
        this.notificar("error", "Informe a data prevista de recebimento do material.");
        return false;
      }
      return true;
    },
    notificar(tipo, mensagem) {
      this.$emit("notificar", { tipo, mensagem });
    },
    async salvar() {
      if (!this.validar()) return;
      this.salvando = true;
      try {
        const payload = {
          nf_entrada: String(this.form.nf_entrada).trim(),
        };
        if (this.podeEditarDataPrevista) {
          payload.dt_prevista_recebimento_material =
            this.form.dt_prevista_recebimento_material;
        }
        const atualizada = await atualizarCabecalhoOsAssistenciaPosVenda(
          this.osId,
          payload,
        );
        this.os = atualizada;
        this.notificar("success", "Dados da OS atualizados.");
        this.$emit("salvo", atualizada);
      } catch (error) {
        console.error(error);
        const msg =
          error.response?.data?.error ||
          error.response?.data?.message ||
          (error.response?.data?.errors &&
            JSON.stringify(error.response.data.errors)) ||
          error.message ||
          "Erro ao salvar.";
        this.notificar("error", msg);
      } finally {
        this.salvando = false;
      }
    },
  },
};
</script>

<style scoped>
.modal-conteudo {
  box-sizing: border-box;
  align-self: center;
  background: var(--cor-bg);
  border-radius: 8px;
  padding: 1.5rem;
  width: min(45rem, calc(100vw - 2rem));
  min-width: min(18rem, calc(100vw - 2rem));
  max-height: 90vh;
  overflow-y: auto;
}
.modal-conteudo h3 {
  margin: 0 0 1rem;
  font-size: 1.2rem;
}
.loading-estado,
.erro-estado {
  margin: 1rem 0;
  color: var(--cor-fonte-fraca, #666);
}
.erro-estado {
  color: var(--cor-erro, #c62828);
}
.formulario {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1rem;
}
.grid-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
@media (max-width: 520px) {
  .grid-info {
    grid-template-columns: 1fr;
  }
}
.campos-datas {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  align-items: start;
}
@media (max-width: 520px) {
  .campos-datas {
    grid-template-columns: 1fr;
  }
}
.campo label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.35rem;
}
.obrigatorio {
  color: var(--cor-erro, #c62828);
}
.campo input {
  width: 100%;
  max-width: 100%;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--cor-separador, #ccc);
  border-radius: 6px;
  font: inherit;
}
.data-calculada {
  margin: 0;
  padding: 0.5rem 0.65rem;
  font-size: 1rem;
  font-weight: 500;
  border: 1px solid var(--cor-separador, #ccc);
  border-radius: 6px;
  background: var(--cor-cinza, rgba(0, 0, 0, 0.04));
  color: var(--cor-fonte, #333);
}
.texto-leitura {
  margin: 0;
  padding: 0.5rem 0.65rem;
  border: 1px solid var(--cor-separador, #ccc);
  border-radius: 6px;
  background: var(--cor-cinza, rgba(0, 0, 0, 0.04));
  white-space: pre-wrap;
  min-height: 2.5rem;
}
.hint-data-calculada {
  margin: 0.35rem 0 0;
  font-size: 0.85rem;
  color: var(--cor-fonte-fraca, #666);
}
.texto-na {
  color: var(--cor-fonte-fraca, #888);
  font-size: 0.9rem;
  margin: 0;
}
.tabela-produtos {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
  font-size: 0.9rem;
}
.tabela-produtos col.col-qtd {
  width: 6rem;
}
.tabela-produtos th,
.tabela-produtos td {
  border: 1px solid var(--cor-separador, #ddd);
  padding: 0.35rem 0.5rem;
  vertical-align: middle;
}
.tabela-produtos th.th-qtd,
.tabela-produtos td:nth-child(2) {
  text-align: center;
  white-space: nowrap;
}
.tabela-produtos td.td-produto,
.tabela-produtos th.th-produto {
  text-align: left !important;
}
.acoes {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 0.5rem;
}
</style>
